import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Shield, Book, Code2, Key, Zap, AlertTriangle, CheckCircle,
  Copy, Check, Search, ExternalLink, Menu, X,
  Terminal, Globe, Lock, BarChart3, ArrowRight, Hash,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

/* ── Copy-to-clipboard button ─────────────────────────────────────────── */
function CopyBtn({ text }) {
  const [copied, setCopied] = useState(false);
  const handle = () => {
    navigator.clipboard.writeText(text).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      onClick={handle}
      className="absolute top-3 right-3 p-1.5 rounded-md bg-white/[0.07] hover:bg-white/[0.12] text-slate-500 hover:text-slate-300 transition-all"
      title="Copy"
    >
      {copied ? <Check size={13} className="text-green-400" /> : <Copy size={13} />}
    </button>
  );
}

/* ── Code block ───────────────────────────────────────────────────────── */
function Code({ children, lang = 'bash', className = '' }) {
  return (
    <div className={`relative group rounded-xl overflow-hidden border border-white/[0.07] ${className}`}>
      {lang && (
        <div className="flex items-center justify-between px-4 py-2 bg-[#080d18] border-b border-white/[0.06]">
          <span className="text-[10px] font-semibold text-slate-600 uppercase tracking-widest">{lang}</span>
        </div>
      )}
      <div className="relative bg-[#050810]">
        <CopyBtn text={children} />
        <pre className="overflow-x-auto p-4 pr-12 text-[13px] leading-relaxed text-slate-300 font-mono">
          <code>{children}</code>
        </pre>
      </div>
    </div>
  );
}

/* ── Inline code ──────────────────────────────────────────────────────── */
function Inline({ children }) {
  return (
    <code className="px-1.5 py-0.5 rounded-md bg-[#00E5FF]/10 text-[#00E5FF] text-[12px] font-mono border border-[#00E5FF]/15">
      {children}
    </code>
  );
}

/* ── Badge ────────────────────────────────────────────────────────────── */
function Badge({ children, color = 'cyan' }) {
  const styles = {
    cyan:   'bg-[#00E5FF]/10 text-[#00E5FF] border-[#00E5FF]/20',
    purple: 'bg-[#7C3AED]/10 text-[#7C3AED] border-[#7C3AED]/20',
    green:  'bg-green-500/10 text-green-400 border-green-500/20',
    red:    'bg-red-500/10 text-red-400 border-red-500/20',
    amber:  'bg-amber-500/10 text-amber-400 border-amber-500/20',
    slate:  'bg-white/[0.06] text-slate-400 border-white/10',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border ${styles[color]}`}>
      {children}
    </span>
  );
}

/* ── HTTP method badge ────────────────────────────────────────────────── */
function Method({ m }) {
  const c = { GET: 'green', POST: 'cyan', DELETE: 'red', PUT: 'amber' };
  return <Badge color={c[m] || 'slate'}>{m}</Badge>;
}


/* ── Section heading with anchor ──────────────────────────────────────── */
function H2({ id, children }) {
  return (
    <h2
      id={id}
      className="group text-2xl font-bold text-white mt-14 mb-5 flex items-center gap-3 scroll-mt-20"
    >
      {children}
      <a href={`#${id}`} className="opacity-0 group-hover:opacity-40 transition-opacity">
        <Hash size={16} className="text-slate-500" />
      </a>
    </h2>
  );
}

function H3({ id, children }) {
  return (
    <h3
      id={id}
      className="group text-lg font-bold text-white mt-9 mb-4 flex items-center gap-2 scroll-mt-20"
    >
      {children}
      <a href={`#${id}`} className="opacity-0 group-hover:opacity-40 transition-opacity">
        <Hash size={13} className="text-slate-500" />
      </a>
    </h3>
  );
}

/* ── Alert box ────────────────────────────────────────────────────────── */
function Alert({ type = 'info', children }) {
  const map = {
    info:    { icon: Zap,           border: 'border-[#00E5FF]/20', bg: 'bg-[#00E5FF]/5', icon_c: 'text-[#00E5FF]' },
    warning: { icon: AlertTriangle, border: 'border-amber-500/20', bg: 'bg-amber-500/5', icon_c: 'text-amber-400' },
    success: { icon: CheckCircle,   border: 'border-green-500/20', bg: 'bg-green-500/5', icon_c: 'text-green-400' },
    danger:  { icon: AlertTriangle, border: 'border-red-500/20',   bg: 'bg-red-500/5',   icon_c: 'text-red-400'   },
  };
  const { icon: Icon, border, bg, icon_c } = map[type];
  return (
    <div className={`flex gap-3 rounded-xl border ${border} ${bg} p-4 my-5 text-sm text-slate-400 leading-relaxed`}>
      <Icon size={16} className={`${icon_c} flex-shrink-0 mt-0.5`} />
      <div>{children}</div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   Sidebar nav structure
───────────────────────────────────────────────────────────────────────── */
const SECTIONS = [
  {
    group: 'Getting Started',
    icon: Book,
    items: [
      { id: 'overview',      label: 'Overview' },
      { id: 'quickstart',    label: 'Quick Start' },
      { id: 'authentication',label: 'Authentication' },
      { id: 'base-url',      label: 'Base URL & Versioning' },
    ],
  },
  {
    group: 'API Reference',
    icon: Code2,
    items: [
      { id: 'scan-wallet',   label: 'Scan Wallet' },
      { id: 'scan-history',  label: 'Scan History' },
      { id: 'api-keys',      label: 'API Keys' },
    ],
  },
  {
    group: 'Concepts',
    icon: BarChart3,
    items: [
      { id: 'risk-scoring',  label: 'Risk Scoring' },
      { id: 'warnings',      label: 'Warning Flags' },
      { id: 'scam-tokens',   label: 'Scam Token Detection' },
    ],
  },
  {
    group: 'Platform',
    icon: Globe,
    items: [
      { id: 'rate-limits',   label: 'Rate Limits' },
      { id: 'errors',        label: 'Error Codes' },
      { id: 'changelog',     label: 'Changelog' },
    ],
  },
];

/* ─────────────────────────────────────────────────────────────────────────
   Main Docs page
───────────────────────────────────────────────────────────────────────── */
export default function Docs() {
  const [activeId,    setActiveId]    = useState('overview');
  const [mobileOpen,  setMobileOpen]  = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const contentRef = useRef(null);
  const location = useLocation();

  /* Scroll to the section named in the URL hash (e.g. /docs#changelog).
     Runs on mount and whenever the hash changes, after the content renders. */
  useEffect(() => {
    const id = location.hash.replace('#', '');
    if (!id) return;
    const t = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 120);
    return () => clearTimeout(t);
  }, [location.hash]);

  /* Highlight active section on scroll */
  useEffect(() => {
    const ids = SECTIONS.flatMap(s => s.items.map(i => i.id));
    const obs = new IntersectionObserver(
      entries => {
        const visible = entries.filter(e => e.isIntersecting);
        if (visible.length > 0) setActiveId(visible[0].target.id);
      },
      { rootMargin: '-80px 0px -60% 0px', threshold: 0 }
    );
    ids.forEach(id => {
      const el = document.getElementById(id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, []);

  const scrollTo = (id) => {
    setMobileOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const allItems = SECTIONS.flatMap(s => s.items);
  const filtered = searchQuery
    ? allItems.filter(i => i.label.toLowerCase().includes(searchQuery.toLowerCase()))
    : [];

  /* ── Sidebar ─────────────────────────────────────── */
  const Sidebar = () => (
    <aside className="w-64 flex-shrink-0 flex flex-col border-r border-white/[0.06] bg-[#080c16] overflow-y-auto">
      {/* Search */}
      <div className="p-4 border-b border-white/[0.06]">
        <div className="flex items-center gap-2 bg-white/[0.04] border border-white/[0.07] rounded-lg px-3 py-2">
          <Search size={13} className="text-slate-600" />
          <input
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search docs…"
            className="bg-transparent text-xs text-slate-300 placeholder-slate-600 outline-none flex-1 w-full"
          />
        </div>
        {/* Dropdown results */}
        {searchQuery && filtered.length > 0 && (
          <div className="mt-2 rounded-lg border border-white/[0.07] bg-[#080d18] overflow-hidden shadow-xl">
            {filtered.map(item => (
              <button
                key={item.id}
                onClick={() => { scrollTo(item.id); setSearchQuery(''); }}
                className="w-full text-left px-3 py-2 text-xs text-slate-400 hover:text-[#00E5FF] hover:bg-[#00E5FF]/5 transition-colors"
              >
                {item.label}
              </button>
            ))}
          </div>
        )}
        {searchQuery && filtered.length === 0 && (
          <p className="mt-2 text-xs text-slate-600 text-center py-1">No results</p>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 py-4 px-3 space-y-5">
        {SECTIONS.map(({ group, icon: Icon, items }) => (
          <div key={group}>
            <div className="flex items-center gap-2 px-2 mb-2">
              <Icon size={11} className="text-slate-600" />
              <span className="text-[10px] font-bold text-slate-600 uppercase tracking-widest">{group}</span>
            </div>
            <ul className="space-y-0.5">
              {items.map(({ id, label }) => (
                <li key={id}>
                  <button
                    onClick={() => scrollTo(id)}
                    className={[
                      'w-full text-left px-3 py-2 rounded-lg text-xs font-medium transition-all',
                      activeId === id
                        ? 'bg-[#00E5FF]/10 text-[#00E5FF] border border-[#00E5FF]/20'
                        : 'text-slate-500 hover:text-slate-200 hover:bg-white/[0.04] border border-transparent',
                    ].join(' ')}
                  >
                    {label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      {/* Bottom CTA */}
      <div className="p-4 border-t border-white/[0.06]">
        <Link
          to="/register"
          className="flex items-center justify-center gap-1.5 w-full bg-[#00E5FF] hover:bg-[#00cce5] text-black text-xs font-bold py-2.5 rounded-lg transition-all"
        >
          Start Free <ArrowRight size={12} />
        </Link>
      </div>
    </aside>
  );

  /* ── Content ─────────────────────────────────────── */
  return (
    <div className="min-h-screen bg-[#0B0F19] text-white">
      <Navbar />

      <div className="pt-16 flex h-[calc(100vh-64px)] sticky top-16">

        {/* Mobile sidebar overlay */}
        {mobileOpen && (
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-30 lg:hidden"
            onClick={() => setMobileOpen(false)}
          />
        )}

        {/* Desktop sidebar */}
        <div className="hidden lg:flex">
          <Sidebar />
        </div>

        {/* Mobile sidebar */}
        <div className={`fixed inset-y-0 left-0 z-40 w-72 lg:hidden transition-transform duration-300 ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}>
          <div className="h-full pt-16 flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 bg-[#080c16] border-b border-white/[0.06]">
              <span className="text-sm font-semibold text-slate-300">Documentation</span>
              <button onClick={() => setMobileOpen(false)} className="text-slate-500 hover:text-slate-300">
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 bg-[#080c16] border-r border-white/[0.06] overflow-y-auto">
              <Sidebar />
            </div>
          </div>
        </div>

        {/* Main content */}
        <main ref={contentRef} className="flex-1 overflow-y-auto">

          {/* Mobile top bar */}
          <div className="sticky top-0 z-20 flex items-center gap-3 px-4 py-3 bg-[#080c16]/95 backdrop-blur border-b border-white/[0.06] lg:hidden">
            <button onClick={() => setMobileOpen(true)} className="text-slate-500 hover:text-slate-300">
              <Menu size={18} />
            </button>
            <span className="text-sm text-slate-400 capitalize">{activeId.replace(/-/g, ' ')}</span>
          </div>

          <div className="max-w-4xl mx-auto px-6 sm:px-10 py-12 pb-28">

            {/* ── Page header ── */}
            <div className="mb-14 pb-10 border-b border-white/[0.06]">
              <div className="inline-flex items-center gap-2 text-xs font-semibold text-[#00E5FF] bg-[#00E5FF]/10 border border-[#00E5FF]/20 px-3 py-1.5 rounded-full mb-5">
                <Book size={11} /> Documentation · v1.0
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">ZenithCipher API Docs</h1>
              <p className="text-slate-400 text-lg max-w-2xl leading-relaxed">
                Everything you need to integrate wallet risk scanning into your application.
                Secure, fast, and built for Web3 teams.
              </p>
              <div className="flex flex-wrap gap-3 mt-6">
                <Badge color="green">REST API</Badge>
                <Badge color="cyan">JSON Responses</Badge>
                <Badge color="purple">JWT Auth</Badge>
                <Badge color="amber">Rate Limited</Badge>
              </div>
            </div>

            {/* ══════════════════════════════════════════════════════════
                GETTING STARTED
            ══════════════════════════════════════════════════════════ */}

            {/* OVERVIEW */}
            <section id="overview">
              <H2 id="overview">
                <Book size={20} className="text-[#00E5FF]" />
                Overview
              </H2>
              <p className="text-slate-400 leading-relaxed mb-5">
                ZenithCipher is a Web3 security intelligence platform that lets you analyze
                Ethereum wallet addresses for risk signals, suspicious activity, and fraud indicators.
                Our API queries the Ethereum blockchain in real-time via Etherscan and runs a
                multi-factor risk engine to produce a structured security report.
              </p>
              <div className="grid sm:grid-cols-3 gap-4 my-7">
                {[
                  { icon: Zap,      title: 'Sub-second Scans', desc: 'Real-time blockchain queries with results in under 1 second.' },
                  { icon: Shield,   title: 'Risk Engine v2',   desc: 'Multi-factor scoring: volume, patterns, token fraud, bot behavior.' },
                  { icon: Lock,     title: 'Secure by Default',desc: 'JWT authentication, CORS protection, and rate limiting built in.' },
                ].map(({ icon: Icon, title, desc }) => (
                  <div key={title} className="glass-card p-5 border border-white/[0.06]">
                    <Icon size={16} className="text-[#00E5FF] mb-3" />
                    <div className="text-sm font-semibold text-white mb-1">{title}</div>
                    <div className="text-xs text-slate-500 leading-relaxed">{desc}</div>
                  </div>
                ))}
              </div>
            </section>

            {/* QUICK START */}
            <section id="quickstart">
              <H2 id="quickstart">
                <Terminal size={20} className="text-[#00E5FF]" />
                Quick Start
              </H2>
              <p className="text-slate-400 leading-relaxed mb-5">
                Scan a wallet in seconds — no account required for public scans.
                Create a free account to persist scan history and get an API key.
              </p>

              <H3 id="qs-curl">1. Scan a wallet (unauthenticated)</H3>
              <p className="text-slate-500 text-sm mb-3">
                Any Ethereum address can be scanned without authentication.
                Results will not be saved to your history.
              </p>
              <Code lang="bash">{`curl https://api.zenithcipher.io/api/scan/0x742d35Cc6634C0532925a3b844Bc454e4438f44e`}</Code>

              <H3 id="qs-auth">2. Scan with your API key (authenticated)</H3>
              <p className="text-slate-500 text-sm mb-3">
                Pass your Bearer token to save the report to your scan history.
              </p>
              <Code lang="bash">{`curl https://api.zenithcipher.io/api/scan/0x742d35Cc6634C0532925a3b844Bc454e4438f44e \\
  -H "Authorization: Bearer YOUR_API_KEY"`}</Code>

              <H3 id="qs-js">3. JavaScript / Node.js example</H3>
              <Code lang="javascript">{`const response = await fetch(
  'https://api.zenithcipher.io/api/scan/0x742d35Cc6634C0532925a3b844Bc454e4438f44e',
  {
    headers: {
      'Authorization': 'Bearer YOUR_API_KEY',
    },
  }
);

const report = await response.json();

console.log(report.riskLevel);   // "High" | "Medium" | "Low"
console.log(report.riskScore);   // 0–100
console.log(report.warnings);    // string[]`}</Code>

              <H3 id="qs-response">4. Sample response</H3>
              <Code lang="json">{`{
  "wallet":          "0x742d35Cc6634C0532925a3b844Bc454e4438f44e",
  "balanceETH":      1.2345,
  "txCount":         312,
  "tokenTransfers":  87,
  "contractCalls":   54,
  "riskScore":       78,
  "riskLevel":       "High",
  "reputationScore": 22,
  "warnings": [
    "High transaction volume",
    "Heavy contract usage"
  ],
  "scamTokens": [
    { "name": "ClaimReward",  "symbol": "CLAIM" },
    { "name": "AirdropToken", "symbol": "ADROP" }
  ],
  "transactions": [ /* last 10 transactions */ ],
  "date": "2026-05-01T12:00:00.000Z"
}`}</Code>
            </section>

            {/* AUTHENTICATION */}
            <section id="authentication">
              <H2 id="authentication">
                <Key size={20} className="text-[#00E5FF]" />
                Authentication
              </H2>
              <p className="text-slate-400 leading-relaxed mb-5">
                ZenithCipher uses <strong className="text-white">Bearer token authentication</strong>.
                Your API key is a signed JWT issued when you register or create a key in the dashboard.
              </p>

              <Alert type="info">
                All endpoints that save data (scan history, API key management) require a valid Bearer token.
                The <Inline>/api/scan/:wallet</Inline> endpoint works without a token but will not persist the result.
              </Alert>

              <H3 id="auth-header">Authorization header</H3>
              <Code lang="http">{`Authorization: Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...`}</Code>

              <H3 id="auth-register">Register (get a token)</H3>
              <Code lang="bash">{`curl -X POST https://api.zenithcipher.io/api/auth/register \\
  -H "Content-Type: application/json" \\
  -d '{ "email": "you@example.com", "password": "yourpassword" }'`}</Code>

              <H3 id="auth-login">Login (get a token)</H3>
              <Code lang="bash">{`curl -X POST https://api.zenithcipher.io/api/auth/login \\
  -H "Content-Type: application/json" \\
  -d '{ "email": "you@example.com", "password": "yourpassword" }'`}</Code>

              <p className="text-slate-400 text-sm mt-4 mb-2">Both return:</p>
              <Code lang="json">{`{
  "token": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...",
  "user": {
    "id":    "6630f1e2abc...",
    "email": "you@example.com",
    "plan":  "free"
  }
}`}</Code>

              <Alert type="warning">
                Store your token securely. Do not expose it in client-side code or public repositories.
                Rotate your key immediately from the dashboard if you suspect it has been compromised.
              </Alert>
            </section>

            {/* BASE URL */}
            <section id="base-url">
              <H2 id="base-url">
                <Globe size={20} className="text-[#00E5FF]" />
                Base URL &amp; Versioning
              </H2>
              <p className="text-slate-400 leading-relaxed mb-4">
                All API requests should be made to the following base URL. We use URL path versioning.
                The current stable version is <Inline>v1</Inline>.
              </p>
              <Code lang="text">{`Production:  https://api.zenithcipher.io
Development: http://localhost:5000`}</Code>

              <div className="mt-6 glass-card border border-white/[0.06] overflow-hidden">
                <div className="px-5 py-3 border-b border-white/[0.06] bg-white/[0.02]">
                  <span className="text-xs font-semibold text-slate-400 uppercase tracking-widest">Endpoint Summary</span>
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[11px] text-slate-600 uppercase tracking-wider border-b border-white/[0.05]">
                      <th className="text-left px-5 py-3 font-medium">Method</th>
                      <th className="text-left px-5 py-3 font-medium">Path</th>
                      <th className="text-left px-5 py-3 font-medium">Auth</th>
                      <th className="text-left px-5 py-3 font-medium">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/[0.04]">
                    {[
                      { m: 'POST', path: '/api/auth/register',   auth: false, desc: 'Create account' },
                      { m: 'POST', path: '/api/auth/login',      auth: false, desc: 'Login and get token' },
                      { m: 'GET',  path: '/api/auth/me',         auth: true,  desc: 'Get current user' },
                      { m: 'GET',  path: '/api/scan/:wallet',    auth: false, desc: 'Scan a wallet address' },
                      { m: 'GET',  path: '/api/my-scans',        auth: true,  desc: 'Fetch scan history' },
                    ].map(({ m, path, auth, desc }) => (
                      <tr key={path} className="hover:bg-white/[0.02] transition-colors">
                        <td className="px-5 py-3"><Method m={m} /></td>
                        <td className="px-5 py-3 font-mono text-xs text-slate-300">{path}</td>
                        <td className="px-5 py-3">
                          {auth
                            ? <Badge color="cyan">Required</Badge>
                            : <Badge color="slate">Optional</Badge>
                          }
                        </td>
                        <td className="px-5 py-3 text-slate-500 text-xs">{desc}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* ══════════════════════════════════════════════════════════
                API REFERENCE
            ══════════════════════════════════════════════════════════ */}

            {/* SCAN WALLET */}
            <section id="scan-wallet">
              <H2 id="scan-wallet">
                <Search size={20} className="text-[#00E5FF]" />
                Scan Wallet
              </H2>

              <div className="flex items-center gap-3 mb-5 p-4 rounded-xl bg-white/[0.03] border border-white/[0.07]">
                <Method m="GET" />
                <code className="text-sm text-slate-300 font-mono">/api/scan/<span className="text-[#00E5FF]">:wallet</span></code>
                <div className="ml-auto"><Badge color="slate">Optional Auth</Badge></div>
              </div>

              <p className="text-slate-400 text-sm leading-relaxed mb-6">
                Analyzes an Ethereum wallet address and returns a comprehensive risk report.
                When called with a valid Bearer token, the report is automatically saved to your scan history.
              </p>

              <H3 id="scan-params">Path Parameters</H3>
              <div className="glass-card border border-white/[0.06] overflow-hidden mb-6">
                <table className="w-full">
                  <thead>
                    <tr className="text-[11px] text-slate-600 uppercase tracking-wider border-b border-white/[0.05]">
                      <th className="text-left px-5 py-3 font-medium">Parameter</th>
                      <th className="text-left px-5 py-3 font-medium">Type</th>
                      <th className="text-left px-5 py-3 font-medium">Description</th>
                    </tr>
                  </thead>
                  <tbody className="px-5">
                    <tr>
                      <td className="px-5 py-3"><Inline>wallet</Inline> <span className="ml-1 text-[10px] text-red-400 font-semibold">required</span></td>
                      <td className="px-5 py-3 font-mono text-xs text-[#7C3AED]">string</td>
                      <td className="px-5 py-3 text-xs text-slate-500">A valid Ethereum address starting with <Inline>0x</Inline>, exactly 42 characters (hex).</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <H3 id="scan-response">Response Fields</H3>
              <div className="glass-card border border-white/[0.06] overflow-hidden mb-6">
                <table className="w-full">
                  <thead>
                    <tr className="text-[11px] text-slate-600 uppercase tracking-wider border-b border-white/[0.05]">
                      <th className="text-left px-5 py-3 font-medium">Field</th>
                      <th className="text-left px-5 py-3 font-medium">Type</th>
                      <th className="text-left px-5 py-3 font-medium">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/[0.04]">
                    {[
                      { name: 'wallet',          type: 'string',   desc: 'The queried wallet address.' },
                      { name: 'balanceETH',       type: 'number',   desc: 'Current ETH balance, rounded to 4 decimal places.' },
                      { name: 'txCount',          type: 'number',   desc: 'Total number of transactions (inbound + outbound).' },
                      { name: 'tokenTransfers',   type: 'number',   desc: 'Number of ERC-20 token transfer events.' },
                      { name: 'contractCalls',    type: 'number',   desc: 'Transactions that include non-zero input data (contract interactions).' },
                      { name: 'riskScore',        type: 'number',   desc: 'Composite risk score 0–100. Higher = more risky.' },
                      { name: 'riskLevel',        type: 'string',   desc: '"Low" (0–30), "Medium" (31–60), or "High" (61–100).' },
                      { name: 'reputationScore',  type: 'number',   desc: 'Inverse of riskScore: 100 − riskScore.' },
                      { name: 'warnings',         type: 'string[]', desc: 'Human-readable risk signals detected.' },
                      { name: 'scamTokens',       type: 'object[]', desc: 'Tokens with suspicious names/symbols (name, symbol).' },
                      { name: 'transactions',     type: 'object[]', desc: 'Last 10 transactions from Etherscan.' },
                      { name: 'date',             type: 'string',   desc: 'ISO 8601 timestamp of when the scan was performed.' },
                    ].map(({ name, type, desc }) => (
                      <tr key={name} className="hover:bg-white/[0.02] transition-colors">
                        <td className="px-5 py-3"><Inline>{name}</Inline></td>
                        <td className="px-5 py-3 font-mono text-xs text-[#7C3AED]">{type}</td>
                        <td className="px-5 py-3 text-xs text-slate-500">{desc}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <H3 id="scan-errors">Error Responses</H3>
              <Code lang="json">{`// 400 — Invalid wallet address
{ "error": "Invalid Ethereum wallet address" }

// 500 — Etherscan API or network issue
{ "error": "Scan failed - API or network issue" }`}</Code>
            </section>

            {/* SCAN HISTORY */}
            <section id="scan-history">
              <H2 id="scan-history">
                <BarChart3 size={20} className="text-[#00E5FF]" />
                Scan History
              </H2>

              <div className="flex items-center gap-3 mb-5 p-4 rounded-xl bg-white/[0.03] border border-white/[0.07]">
                <Method m="GET" />
                <code className="text-sm text-slate-300 font-mono">/api/my-scans</code>
                <div className="ml-auto"><Badge color="cyan">Auth Required</Badge></div>
              </div>

              <p className="text-slate-400 text-sm leading-relaxed mb-6">
                Returns up to 50 most recent scan reports for the authenticated user, sorted newest first.
              </p>

              <H3 id="history-response">Response</H3>
              <p className="text-slate-500 text-sm mb-3">Returns an array of scan report objects (same shape as the scan endpoint).</p>
              <Code lang="json">{`[
  {
    "_id":             "6630f1e2abc123def456789",
    "userId":          "6630f1e2abc...",
    "wallet":          "0x742d35Cc...",
    "balanceETH":      1.2345,
    "txCount":         312,
    "tokenTransfers":  87,
    "contractInteractions": 54,
    "riskScore":       78,
    "riskLevel":       "High",
    "reputationScore": 22,
    "warnings":        ["High transaction volume"],
    "scamTokens":      [],
    "date":            "2026-05-01T12:00:00.000Z"
  },
  ...
]`}</Code>

              <Alert type="info">
                Scan history is only populated when you scan wallets while authenticated.
                Unauthenticated scans are not saved.
              </Alert>
            </section>

            {/* API KEYS */}
            <section id="api-keys">
              <H2 id="api-keys">
                <Key size={20} className="text-[#00E5FF]" />
                API Keys
              </H2>
              <p className="text-slate-400 text-sm leading-relaxed mb-5">
                Your JWT token doubles as your API key. Generate and manage keys from the
                <Link to="/dashboard/api" className="text-[#00E5FF] hover:underline ml-1">API Access</Link> page
                in your dashboard. Keys are scoped to your account and inherit your plan's rate limits.
              </p>

              <div className="grid sm:grid-cols-3 gap-4 my-6">
                {[
                  { plan: 'Free',       keys: 1,   color: 'slate' },
                  { plan: 'Starter',    keys: 3,   color: 'cyan'  },
                  { plan: 'Pro',        keys: 10,  color: 'purple' },
                ].map(({ plan, keys, color }) => (
                  <div key={plan} className="glass-card p-5 border border-white/[0.06] text-center">
                    <Badge color={color}>{plan}</Badge>
                    <div className="text-2xl font-bold text-white mt-3 mb-0.5">{keys}</div>
                    <div className="text-xs text-slate-500">API key{keys > 1 ? 's' : ''}</div>
                  </div>
                ))}
              </div>
            </section>

            {/* ══════════════════════════════════════════════════════════
                CONCEPTS
            ══════════════════════════════════════════════════════════ */}

            {/* RISK SCORING */}
            <section id="risk-scoring">
              <H2 id="risk-scoring">
                <Shield size={20} className="text-[#00E5FF]" />
                Risk Scoring
              </H2>
              <p className="text-slate-400 text-sm leading-relaxed mb-6">
                The ZenithCipher Risk Engine v2.0 computes a composite score from 0–100 by
                evaluating several on-chain behavioral signals. Multiple signals stack additively
                and are capped at 100.
              </p>

              <div className="glass-card border border-white/[0.06] overflow-hidden mb-6">
                <div className="px-5 py-3 border-b border-white/[0.06] bg-white/[0.02]">
                  <span className="text-xs font-semibold text-slate-400 uppercase tracking-widest">Risk Signals</span>
                </div>
                <table className="w-full">
                  <thead>
                    <tr className="text-[11px] text-slate-600 uppercase tracking-wider border-b border-white/[0.05]">
                      <th className="text-left px-5 py-3 font-medium">Condition</th>
                      <th className="text-left px-5 py-3 font-medium">Points Added</th>
                      <th className="text-left px-5 py-3 font-medium">Warning Label</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/[0.04]">
                    {[
                      { cond: 'txCount > 200',          pts: '+25', label: 'High transaction volume' },
                      { cond: 'contractCalls > 50',      pts: '+25', label: 'Heavy contract usage' },
                      { cond: 'tokenTransfers > 100',    pts: '+30', label: 'Token spam activity' },
                      { cond: 'txCount > 500',           pts: '+40', label: 'Bot-like patterns' },
                      { cond: 'txCount < 3 & ETH < 0.01',pts: '+20', label: 'Burner wallet traits' },
                      { cond: 'tokenTransfers > 300',    pts: '+40', label: 'Token farming risk' },
                    ].map(({ cond, pts, label }) => (
                      <tr key={cond} className="hover:bg-white/[0.02] transition-colors">
                        <td className="px-5 py-3"><Inline>{cond}</Inline></td>
                        <td className="px-5 py-3">
                          <span className="text-red-400 font-bold font-mono text-sm">{pts}</span>
                        </td>
                        <td className="px-5 py-3 text-xs text-slate-500">{label}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <H3 id="risk-levels">Risk Levels</H3>
              <div className="grid grid-cols-3 gap-4">
                {[
                  { level: 'Low',    range: '0 – 30',  color: 'green',  desc: 'Normal wallet behavior. No major red flags detected.' },
                  { level: 'Medium', range: '31 – 60', color: 'amber',  desc: 'Some unusual activity. Review warnings before transacting.' },
                  { level: 'High',   range: '61 – 100',color: 'red',    desc: 'Multiple risk signals. High probability of suspicious activity.' },
                ].map(({ level, range, color, desc }) => (
                  <div key={level} className="glass-card p-4 border border-white/[0.06]">
                    <Badge color={color}>{level}</Badge>
                    <div className="text-base font-bold text-white mt-3 mb-1 font-mono">{range}</div>
                    <div className="text-xs text-slate-500 leading-relaxed">{desc}</div>
                  </div>
                ))}
              </div>
            </section>

            {/* WARNINGS */}
            <section id="warnings">
              <H2 id="warnings">
                <AlertTriangle size={20} className="text-[#00E5FF]" />
                Warning Flags
              </H2>
              <p className="text-slate-400 text-sm leading-relaxed mb-5">
                The <Inline>warnings</Inline> array in the scan response contains human-readable strings
                describing each risk signal that was triggered. A wallet can have zero or multiple warnings.
              </p>
              <div className="space-y-2">
                {[
                  { w: 'High transaction volume',   desc: 'Wallet has made more than 200 transactions. Could indicate high activity, bots, or spam.' },
                  { w: 'Heavy contract usage',       desc: 'More than 50 contract interactions detected. Common in DeFi exploiters and bots.' },
                  { w: 'Token spam activity',        desc: 'Over 100 token transfer events. Often associated with airdrop farming.' },
                  { w: 'Bot-like patterns',          desc: 'Transaction count exceeds 500 — strong indicator of automated bot behavior.' },
                  { w: 'Burner wallet traits',       desc: 'Very low ETH balance and fewer than 3 transactions. Typical one-time use wallet.' },
                  { w: 'Token farming risk',         desc: 'Over 300 token transfers. High confidence of systematic token farming.' },
                ].map(({ w, desc }) => (
                  <div key={w} className="flex gap-3 p-4 glass-card border border-white/[0.06] rounded-xl">
                    <AlertTriangle size={14} className="text-amber-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="text-sm font-semibold text-white mb-0.5">{w}</div>
                      <div className="text-xs text-slate-500 leading-relaxed">{desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* SCAM TOKENS */}
            <section id="scam-tokens">
              <H2 id="scam-tokens">
                <AlertTriangle size={20} className="text-[#00E5FF]" />
                Scam Token Detection
              </H2>
              <p className="text-slate-400 text-sm leading-relaxed mb-5">
                The <Inline>scamTokens</Inline> array lists ERC-20 tokens received by the wallet
                whose name or symbol matches known scam patterns. These tokens are commonly airdropped
                to wallets to lure users into visiting phishing sites.
              </p>

              <H3 id="scam-patterns">Detection Patterns</H3>
              <p className="text-slate-500 text-sm mb-3">A token is flagged if its name or symbol contains any of:</p>
              <div className="flex flex-wrap gap-2 mb-6">
                {['"claim"', '"reward"', '"airdrop"', '"CLAIM" (symbol)'].map(p => (
                  <Inline key={p}>{p}</Inline>
                ))}
              </div>

              <H3 id="scam-object">ScamToken Object</H3>
              <Code lang="json">{`{
  "name":   "ClaimReward Token",
  "symbol": "CLAIM"
}`}</Code>

              <Alert type="warning">
                The presence of scam tokens does not necessarily mean the wallet is controlled by a scammer.
                Wallets often receive airdropped scam tokens without the owner's knowledge.
                Use scam token count as one signal among many, not as a definitive flag.
              </Alert>
            </section>

            {/* ══════════════════════════════════════════════════════════
                PLATFORM
            ══════════════════════════════════════════════════════════ */}

            {/* RATE LIMITS */}
            <section id="rate-limits">
              <H2 id="rate-limits">
                <Zap size={20} className="text-[#00E5FF]" />
                Rate Limits
              </H2>
              <p className="text-slate-400 text-sm leading-relaxed mb-6">
                Rate limits are applied per IP address on a rolling 15-minute window.
                Authenticated requests use your account's plan limits.
              </p>

              <div className="glass-card border border-white/[0.06] overflow-hidden mb-6">
                <table className="w-full">
                  <thead>
                    <tr className="text-[11px] text-slate-600 uppercase tracking-wider border-b border-white/[0.05]">
                      <th className="text-left px-5 py-3 font-medium">Plan</th>
                      <th className="text-left px-5 py-3 font-medium">Scans / Day</th>
                      <th className="text-left px-5 py-3 font-medium">Requests / 15 min</th>
                      <th className="text-left px-5 py-3 font-medium">Burst</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/[0.04]">
                    {[
                      { plan: 'Free',       scans: '10',         rpm: '100',  burst: 'No'  },
                      { plan: 'Starter',    scans: '100',        rpm: '500',  burst: 'Yes' },
                      { plan: 'Pro',        scans: 'Unlimited',  rpm: '2,000',burst: 'Yes' },
                      { plan: 'Enterprise', scans: 'Unlimited',  rpm: 'Custom',burst: 'Yes' },
                    ].map(({ plan, scans, rpm, burst }) => (
                      <tr key={plan} className="hover:bg-white/[0.02] transition-colors">
                        <td className="px-5 py-3 text-sm font-medium text-white">{plan}</td>
                        <td className="px-5 py-3 text-sm text-slate-400 tabular-nums">{scans}</td>
                        <td className="px-5 py-3 text-sm text-slate-400 tabular-nums">{rpm}</td>
                        <td className="px-5 py-3">
                          <Badge color={burst === 'Yes' ? 'green' : 'slate'}>{burst}</Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <H3 id="rate-headers">Rate Limit Headers</H3>
              <p className="text-slate-500 text-sm mb-3">Every API response includes standard rate-limit headers:</p>
              <Code lang="http">{`X-RateLimit-Limit:     100
X-RateLimit-Remaining: 87
X-RateLimit-Reset:     1746364200
Retry-After:           60    ← only present on 429 responses`}</Code>

              <Alert type="danger">
                Exceeding your rate limit returns a <Inline>429 Too Many Requests</Inline> response.
                Back off exponentially and retry after the <Inline>Retry-After</Inline> value (in seconds).
              </Alert>
            </section>

            {/* ERRORS */}
            <section id="errors">
              <H2 id="errors">
                <AlertTriangle size={20} className="text-[#00E5FF]" />
                Error Codes
              </H2>
              <p className="text-slate-400 text-sm leading-relaxed mb-6">
                ZenithCipher uses conventional HTTP response codes. All error bodies follow the same shape:
              </p>
              <Code lang="json">{`{
  "error": "Human-readable description of what went wrong"
}`}</Code>

              <div className="mt-6 glass-card border border-white/[0.06] overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="text-[11px] text-slate-600 uppercase tracking-wider border-b border-white/[0.05]">
                      <th className="text-left px-5 py-3 font-medium">Status</th>
                      <th className="text-left px-5 py-3 font-medium">Meaning</th>
                      <th className="text-left px-5 py-3 font-medium">Common Causes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/[0.04]">
                    {[
                      { code: '200 OK',                   color: 'green',  desc: 'Request succeeded.' },
                      { code: '400 Bad Request',           color: 'amber',  desc: 'Invalid wallet address format or missing required fields.' },
                      { code: '401 Unauthorized',          color: 'red',    desc: 'Missing or invalid Authorization header / expired token.' },
                      { code: '403 Forbidden',             color: 'red',    desc: 'Valid token but insufficient permissions for this action.' },
                      { code: '404 Not Found',             color: 'slate',  desc: 'Endpoint does not exist.' },
                      { code: '429 Too Many Requests',     color: 'amber',  desc: 'Rate limit exceeded. Wait for Retry-After seconds.' },
                      { code: '500 Internal Server Error', color: 'red',    desc: 'Etherscan API failure or unexpected server error.' },
                    ].map(({ code, color, desc }) => (
                      <tr key={code} className="hover:bg-white/[0.02] transition-colors">
                        <td className="px-5 py-3"><Badge color={color}>{code}</Badge></td>
                        <td className="px-5 py-3 text-xs text-slate-400 leading-relaxed">{desc}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* CHANGELOG */}
            <section id="changelog">
              <H2 id="changelog">
                <CheckCircle size={20} className="text-[#00E5FF]" />
                Changelog
              </H2>
              <p className="text-slate-400 text-sm leading-relaxed mb-8">
                All notable API and platform changes. ZenithCipher follows <a href="https://semver.org" target="_blank" rel="noopener noreferrer" className="text-[#00E5FF] hover:underline inline-flex items-center gap-0.5">SemVer <ExternalLink size={10} /></a>.
              </p>

              {[
                {
                  version: 'v1.2.0',
                  date: 'May 2026',
                  tag: 'Latest',
                  changes: [
                    'Risk Engine v2.0 — added Token Farming Risk and Bot-like Pattern signals',
                    'New response field: contractCalls (alias for contractInteractions)',
                    'transactions field now returns last 10 tx objects instead of a count',
                    'optionalAuth middleware: authenticated scans now persist to MongoDB',
                    'Added date field to all scan responses',
                  ],
                },
                {
                  version: 'v1.1.0',
                  date: 'March 2026',
                  tag: '',
                  changes: [
                    'SaaS dashboard with full nested routing (DashboardLayout)',
                    'New pages: WalletScannerPage, ScanHistory, SavedReports, Billing, Settings',
                    'AuthContext hydration fix — users no longer logged out on page refresh',
                    'Scam token detection added to scan engine',
                  ],
                },
                {
                  version: 'v1.0.0',
                  date: 'January 2026',
                  tag: '',
                  changes: [
                    'Initial public release',
                    'Wallet scanner with Etherscan v2 API integration',
                    'JWT authentication (register / login / /me)',
                    'Basic Risk Engine v1 — transaction volume and contract interaction scoring',
                    'MongoDB scan report persistence',
                  ],
                },
              ].map(({ version, date, tag, changes }) => (
                <div key={version} className="relative pl-6 mb-10 border-l border-white/[0.08]">
                  <div className="absolute -left-1.5 top-1 w-3 h-3 rounded-full bg-[#00E5FF]/30 border-2 border-[#00E5FF]" />
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-lg font-bold text-white font-mono">{version}</span>
                    {tag && <Badge color="cyan">{tag}</Badge>}
                    <span className="text-xs text-slate-600 ml-auto">{date}</span>
                  </div>
                  <ul className="space-y-2">
                    {changes.map(c => (
                      <li key={c} className="flex items-start gap-2 text-sm text-slate-500">
                        <CheckCircle size={13} className="text-[#00E5FF] flex-shrink-0 mt-0.5" />
                        {c}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}

              {/* Bottom CTA */}
              <div className="mt-14 glass-card border border-[#00E5FF]/15 p-8 text-center rounded-2xl">
                <Shield size={28} className="text-[#00E5FF] mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">Ready to integrate?</h3>
                <p className="text-slate-400 text-sm mb-6 max-w-md mx-auto">
                  Create a free account and start scanning wallets in minutes.
                  No credit card required.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Link
                    to="/register"
                    className="inline-flex items-center justify-center gap-2 bg-[#00E5FF] hover:bg-[#00cce5] text-black font-bold px-6 py-3 rounded-xl transition-all shadow-lg shadow-[#00E5FF]/20 text-sm group"
                  >
                    Get Started Free <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                  </Link>
                  <Link
                    to="/dashboard/api"
                    className="inline-flex items-center justify-center gap-2 border border-white/10 hover:border-white/20 bg-white/[0.04] text-slate-300 font-medium px-6 py-3 rounded-xl transition-all text-sm"
                  >
                    <Key size={13} /> View API Keys
                  </Link>
                </div>
              </div>
            </section>

          </div>

          <Footer />
        </main>
      </div>
    </div>
  );
}
